import { Component, OnInit, OnDestroy, Inject, LOCALE_ID, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { formatDate, isPlatformBrowser } from '@angular/common';

import { AuthManagerService } from 'src/app/services/auth-manager/auth-manager.service';
import { CloseLostPetListingComponent } from 'src/app/modals/close-lost-pet-listing/close-lost-pet-listing.component';
import { AccountType, User } from 'src/app/interfaces/user';
import {
  PetGender,
  PetMissingReportPublic,
  PetMissingReportStatus,
  PetMissingReportType,
  PetSpeciesFilterableType
} from 'src/app/interfaces/pet';
import { Alert } from 'src/app/interfaces/alert';
import { SocialTagsService } from 'src/app/services/common/social-tags.service';
import { PetService } from 'src/app/services/pet/pet.service';
import { HelperService } from 'src/app/shared/helpers/helper.service';
import { CloudinaryService } from 'src/app/services/cloudinary/cloudinary.service';
import { transformationStringFromObject } from '@cloudinary/url-gen';
import { ConfigService } from 'src/app/services/config/config.service';
import { FeatureFlagsService } from 'src/app/services/feature-flag/feature-flag.service';
import { GoogleMapsService } from 'src/app/services/google-maps/google.maps.service';

@Component({
  selector: 'app-missing-pet',
  templateUrl: './missing-pet.component.html',
  styleUrls: ['./missing-pet.component.scss']
})
export class MissingPetComponent implements OnInit, OnDestroy {
  closeLostPetListingModalRef?: NgbModalRef;
  confirmationModalRef?: NgbModalRef;
  isMyPet = false;
  missingPetReport: PetMissingReportPublic;
  shareLinks: any = { facebook: '', twitter: '', whatsapp: '' };
  user: User;
  petMissingReportStatus = PetMissingReportStatus;
  petMissingReportType = PetMissingReportType;
  title: string;
  petGender = PetGender;
  startMapOpened: boolean;
  accountType = AccountType;
  alert: Alert = null;
  currentUrl: string;
  imgUrl: string;
  isBrowser: boolean;
  companyPhoneNumber: string;
  includeLogo: boolean;
  brandName: string;
  useRebrandedLayout: boolean;
  mapsReady = false;

  constructor(
    public authManager: AuthManagerService,
    private modalService: NgbModal,
    private route: ActivatedRoute,
    private socialTagsService: SocialTagsService,
    private router: Router,
    private petService: PetService,
    private helperService: HelperService,
    private cloudinaryService: CloudinaryService,
    private configService: ConfigService,
    private featureFlagService: FeatureFlagsService,
    @Inject(LOCALE_ID) private locale: string,
    @Inject(PLATFORM_ID) private platformId: Object,
    private googleMapsService: GoogleMapsService,
  ) {
    this.missingPetReport = this.route.snapshot.data.missingPetReport as PetMissingReportPublic;
    this.user = this.route.snapshot.data.user as User;
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.companyPhoneNumber = this.configService.getTextFromKey('company-phone-number');
    this.includeLogo = this.configService.getFeatureFlag('isMissingPetImageLogoEnabled');
    this.brandName = this.configService.get('displayName');
    this.useRebrandedLayout = this.featureFlagService.isEnabled('useRebrandedLayout');
    this.googleMapsService.loadGoogleMapsApi().then(() => (this.mapsReady = true)).catch((err) => console.error(err));
  }

  closeListing() {
    this.closeAlert();
    const modalOptions: NgbModalOptions = {
      backdrop: 'static',
      centered: true,
      size: 'md',
    };

    this.closeLostPetListingModalRef = this.modalService.open(CloseLostPetListingComponent, modalOptions);
    this.closeLostPetListingModalRef.componentInstance.pet = this.missingPetReport.pet;
    this.closeLostPetListingModalRef.componentInstance.reportId = this.missingPetReport.id;
    this.closeLostPetListingModalRef.result
        .then((reportStatus: PetMissingReportStatus) => {
          this.missingPetReport.reportStatus = reportStatus;
          if (this.missingPetReport.reportStatus === PetMissingReportStatus.ClosedFound) {
            this.alert = {
              type: 'success',
              message: 'Your lost pet report has been updated successfully.'
            };
          }
        })
        .catch((reason) => {
          if (reason) {
            this.alert = {
              type: 'danger',
              message: 'An error has occurred. Please try again later.'
            };
          }
        });
  }

  closeAlert() {
    this.alert = null;
  }

  private getSpeciesName(speciesFilter: PetSpeciesFilterableType): string {
    if (speciesFilter === PetSpeciesFilterableType.Dog) {
      return 'Dog';
    }
    if (speciesFilter === PetSpeciesFilterableType.Cat) {
      return 'Cat';
    }
    if (speciesFilter === PetSpeciesFilterableType.Horse) {
      return 'Horse';
    }
    return 'Pet';
  }

  ngOnInit(): void {

    const isFound = this.missingPetReport.reportStatus === PetMissingReportStatus.AwaitingClosureFound || this.missingPetReport.reportStatus === PetMissingReportStatus.ClosedFound;

    const socialImageHeaderBgColor = isFound
      ? '#81BB27'
      : this.missingPetReport.reportType === PetMissingReportType.Missing
        ? '#ee7203'
        : '#E62F3C';

    const socialImageHeaderText = isFound
      ? 'FOUND!'
      : this.missingPetReport.reportType === PetMissingReportType.Missing
        ? 'MISSING!'
        : 'STOLEN!';

    // Checking if the user is the keeper of the reported missing pet
    this.isMyPet = this.user && this.user.id === this.missingPetReport.pet.keeperId;

    // Setting the title
    const species = this.getSpeciesName(this.missingPetReport.pet.speciesFilter);
    const reportType = this.missingPetReport.reportType === PetMissingReportType.Missing ? 'missing' : 'stolen';
    
    if (this.isBrowser) {
      const location =
        this.missingPetReport.lastSeen.location.info?.town ||
        this.missingPetReport.lastSeen.location.info?.postcode;
        
      if (location) {
        this.title = `${species} ${reportType} in ${location}`;
      } else {
        this.title = `${species} ${reportType}`;
      }
    } else {
      this.title = `${species} ${reportType}`;
    }

    // Setting map accordion opened or closed
    this.startMapOpened = true;

    // Updating social tags and title
    this.socialTagsService.setTitle(this.title);
    const staticDescription = `${this.brandName} runs a UK wide lost pet service for its microchipped pets.`;
    const lastSeenDate = formatDate(this.missingPetReport.lastSeen.date, 'd MMMM y', this.locale);
    const description = `${this.missingPetReport.pet.name}, ${this.missingPetReport.pet.breed} has been ${reportType} since ${lastSeenDate}. ${staticDescription}`;
    this.socialTagsService.setDescription(description);

    // Cloudinary string transformation created with object syntax
    const socialImageTransformation = transformationStringFromObject([
      {gravity: "face", width: 310, height: 310, crop: 'fill'},
      {
        overlay: {
          font_family: "Lato",
          font_size: 25,
          text: socialImageHeaderText,
          text_align: "center",
        },
        height: 30,
        width: 310,
        crop: "fit",
        background: socialImageHeaderBgColor,
        color: "white",
        gravity: "north",
      },
      ...(this.includeLogo
        ? [{
          overlay: {
            font_family: "Lato",
            font_size: 25,
            text: " ",
            text_align: "center",
          },
          height: 35,
          width: 310,
          crop: "fit",
          background: "#FFFFFF",
          gravity: "south",
        },
        {
          overlay: "Identibase_logo",
          height: 25,
          width: 310,
          crop: "fit",
          background: "#FFFFFF",
          gravity: "south",
          border: "5px_solid_white",
        }]
      : []),
    ])

    const socialImageUrl = this.cloudinaryService.getImageUrlWithTransformation(
      this.missingPetReport.lastSeen.imageId,
      socialImageTransformation
    );

    this.imgUrl = socialImageUrl;

    this.socialTagsService.setImage(socialImageUrl);

    if (this.isBrowser) {
      this.shareLinks = {
        facebook: 'https://www.facebook.com/sharer/sharer.php?u=' + window.location.href,
        twitter: 'https://twitter.com/share?url=' + window.location.href,
        whatsapp: 'https://wa.me/?text=' + window.location.href,
        // whatsapp: 'whatsapp://send?text=' + window.location.href,
      };
    }

    this.currentUrl = this.router.routerState.snapshot.url;
  }

  ngOnDestroy(): void {
    this.closeLostPetListingModalRef?.dismiss('destroyed');
    this.confirmationModalRef?.dismiss('destroyed');
  }
}
